/**
 * Centralized, typed registry of every browser-storage key the application
 * reads or writes.
 *
 * All `localStorage` / `sessionStorage` access for application-owned keys
 * SHOULD reference an entry from here instead of an inline string literal, so
 * each key string is defined in exactly one place and its storage area is
 * documented.
 *
 * Keys flagged `deprecated` are legacy keys that are only ever `remove()`d
 * (one-time purge of values still present in older browsers) and are never
 * written. They are listed here so they are not accidentally reintroduced.
 */

export type StorageScope = 'local' | 'session';

export interface StorageKeyMeta {
  /** The literal storage key string. */
  key: string;
  /** Which Web Storage area the key lives in. */
  storage: StorageScope;
  /** Legacy purge-only key: removed on cleanup, never written. */
  deprecated?: boolean;
}

export const STORAGE_KEYS = {
  CONFIG: { key: 'config', storage: 'local' },
  DEFAULT_CLIENT_ID: { key: 'DEFAULT_CLIENT_ID', storage: 'local' },
  DEFAULT_VEHICLE_ID: { key: 'DEFAULT_VEHICLE_ID', storage: 'local' },
  FRAGA_COMPARISON_LIST: { key: 'fraga-comparission-list', storage: 'local' },
  FRAGA_FAVORITE_ITEMS: { key: 'fraga-favorite-items', storage: 'local' },
  FRAGA_VEHICLE_LINE: { key: 'fraga-vehicle-line', storage: 'local' },
  GLORIA_WIDGET_POSITION: { key: 'gloria-widget-position', storage: 'local' },
  KARBAN_COLUMNS: { key: 'karbanColumns', storage: 'local' },
  LANGUAGE: { key: 'language', storage: 'session' },
  LAST_OS_SESSION_ID: { key: 'lastOsSessionId', storage: 'local' },
  LOGS: { key: 'logs', storage: 'local' },
  ORDER: { key: 'order', storage: 'local' },
  PAGE_SIZE: { key: 'pageSize', storage: 'local', deprecated: true },
  SIDENAV: { key: 'sidenav', storage: 'local' },
  STATES: { key: 'states', storage: 'local', deprecated: true },
  TECINCO_CRM_WIDGET_USER_DATA: { key: 'tecincocrm_widget_user_data', storage: 'local' },
  CASH_TITLE_CACHE: { key: 'cash-title-cache', storage: 'local' },
  READ_MESSAGES: { key: 'readMessages', storage: 'local' },
  CONCILIATION_ADD: { key: 'conciliationAdd', storage: 'local' },
  LEGACY_INFO: { key: 'LEGACY_INFO', storage: 'local' },
  TOOL_AI_CONFIG: { key: 'tecinco-web-MekanicGo-configuracoes-tool-ai-gloria', storage: 'local' },
  LLM_USER_SETTINGS: { key: 'app_llm_user_settings', storage: 'local' },
  MOBILE_OPENING: { key: 'mobile_opening', storage: 'local' },
  CONTA_AZUL_OAUTH_STATE: { key: 'conta_azul_oauth_state', storage: 'session' },
  REVERSE_ROUTER: { key: 'reverse-router', storage: 'local' },
  REPORT_FILTERS: { key: 'report-filters', storage: 'local' },
  INVOICE_LOGS: { key: 'mkg-logs', storage: 'local' },
  CASHIER_LOGS: { key: 'cashier-logs', storage: 'local' },
  STOCK_LOGS: { key: 'stock-logs', storage: 'local' },
} as const satisfies Record<string, StorageKeyMeta>;

/** Union of every registered storage key string. */
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]['key'];
